import {Action, getModule, Module, Mutation, VuexModule} from "vuex-module-decorators";
import store from "@/store";
import {LadingState} from "@/interfaces/state";
import {Lading} from "@/interfaces/entity";
import {
    calculateMoneyLading,
    createLading, deleteLading,
    listLading, listLadingByRowGuids, listLadingNeedUpdate,
    singleLading, singleLadingCodeJoinRelation,
    updateLading
} from "@/modules/ladings/services";
import {IdMapper} from "@/interfaces/state/base";
import {PaginationMeta} from "@/interfaces/common";
import {LadingParams} from "@/modules/ladings/params";
import { union } from 'lodash';
import {stockInWait} from "@/modules/warehouses/services";

@Module({ dynamic: true, name: "lading", store })
class LadingModule extends VuexModule implements LadingState {
    data: IdMapper<Lading> = {};
    ids: string[] = [];
    meta: PaginationMeta = new PaginationMeta();
    params: LadingParams = new LadingParams();

    dataCached: IdMapper<Lading> = {};

    isDirtyForm = false;

    selectedRowLadings = [];

    isNextUpdateLading = false;

    get ladings() {
        return this.ids.map((id: string) => this.data[id]);
    }

    @Mutation
    private SET_NEXT_UPDATE_LADING(payload: any) {
        this.isNextUpdateLading = payload;
    }
    @Mutation
    private UPDATE_SELECTED_ROW_LADINGS(payload) {
        this.selectedRowLadings = payload;
    }

    @Mutation
    private UPDATE_LADING_PARAMS(payload: any) {
        this.params = payload;
    }

    @Mutation
    private MAP_LADING(payload: any) {
        const idMapper: IdMapper<Lading> = {};
        this.meta = payload.meta;
        this.ids = payload.datas?.map((lading: Lading) => {
            idMapper[lading.rowGuid!] = {...lading, isDeleting: false};
            return lading.rowGuid;
        });
        this.data = idMapper;
    }

    @Mutation
    private ADD_LADING(payload: Lading) {
        this.ids = union(this.ids, [payload.rowGuid]);
        this.data = { ...this.data, [payload.rowGuid]: {...payload, isDeleting: false} }
    }

    @Mutation
    private UPDATE_LADING(payload: Lading) {
        this.ids = union(this.ids, [payload.rowGuid]);
        this.data[payload.rowGuid] = { ...payload, isDeleting: false }
    }

    @Mutation
    private DELETE_LADING(payload: Lading) {
        if (!this.data[payload.rowGuid] || !this.ids.includes(payload.rowGuid)) return;
        this.ids = this.ids.filter((id: string) => id !== payload.rowGuid);
        delete this.data[payload.rowGuid];
    }

    @Mutation
    private START_DELETE_LADING(id: string) {
        this.data[id].isDeleting = true;
    }

    @Mutation
    private END_DELETE_LADING(id: string) {
        this.data[id].isDeleting = false;
    }

    @Mutation
    private SET_DIRTY_FORM() {
        this.isDirtyForm = true;
    }

    @Mutation
    private SET_PRISTINE_FORM() {
        this.isDirtyForm = false;
    }

    @Mutation
    private ADD_LADING_DATA_CACHED(lading: Lading) {
        this.dataCached[lading.rowGuid] = lading;
    }

    @Mutation
    private CLEAR_DATA_CACHED() {
        this.dataCached = {} as any;
    }

    @Mutation
    private CLEAR_DATA() {
        this.data = {};
    }

    @Action({ rawError: true })
    public addLading(payload: Lading) {
        this.ADD_LADING(payload);
    }

    @Action({ rawError: true })
    public async createLading(form: any) {
        const { data } = await createLading(form);
        this.ADD_LADING(data.data);
        return data.data;
    }

    @Action({ rawError: true })
    public async listLading(params: LadingParams = new LadingParams()) {
        this.UPDATE_SELECTED_ROW_LADINGS([]);
        const { data } = await listLading(params);
        this.MAP_LADING(data);
    }

    @Action({ rawError: true })
    public async listLadingByRowGuid(rowGuids: string[] = []) {
        const { data } = await listLadingByRowGuids(rowGuids);
        this.MAP_LADING(data);
    }

    @Action({ rawError: true })
    public async singleLading(id: string) {
        const { data } = await singleLading(id);
        this.ADD_LADING(data.data);
        return data.data;
    }

    @Action({ rawError: true })
    public async singleLadingByCode(ladingCode: string) {
        const { data } = await singleLadingCodeJoinRelation(ladingCode);
        this.ADD_LADING(data.data);
        return data.data;
    }

    @Action({ rawError: true })
    public async updateLading(dataReq: any) {
        const { data } = await updateLading(dataReq.rowGuid, dataReq);
        this.UPDATE_LADING(data.data);
        return data.data;
    }

    @Action({ rawError: true })
    public async deleteLading(id: string) {
        this.START_DELETE_LADING(id);
        const { data } = await deleteLading(id);
        this.END_DELETE_LADING(id);
        this.DELETE_LADING(data.data);
    }

    @Action({ rawError: true })
    public async calculateMoney(dataReq: any) {
        const { data } = await calculateMoneyLading(dataReq);
        return data.datas;
    }

    @Action({ rawError: true })
    public updateLadingParams(payload: any) {
        this.UPDATE_LADING_PARAMS(payload);
    }

    @Action({ rawError: true })
    public async listLadingNeedUpdate(params: LadingParams = new LadingParams()) {
        const { data } = await listLadingNeedUpdate(params);
        this.MAP_LADING(data);
    }

    @Action({ rawError: true })
    public removeLading(payload: any) {
        this.DELETE_LADING(payload);
    }

    @Action({ rawError: true })
    public setDirtyForm() {
        this.SET_DIRTY_FORM();
    }

    @Action({ rawError: true })
    public setPristineForm() {
        this.SET_PRISTINE_FORM();
    }
    @Action({ rawError: true })
    public addDataCached(lading: Lading) {
        this.ADD_LADING_DATA_CACHED(lading);
    }
    @Action({ rawError: true })
    public clearDataCached() {
        this.CLEAR_DATA_CACHED();
    }
    @Action({ rawError: true })
    public clearData() {
        this.CLEAR_DATA();
    }

    @Action({ rawError: true })
    public updateSelectedRowLadings(payload: any) {
        this.UPDATE_SELECTED_ROW_LADINGS(payload);
    }

    @Action({ rawError: true })
    public async listLadingStockInWait(params: any) {
        this.UPDATE_SELECTED_ROW_LADINGS([]);
        const { data } = await stockInWait(params);
        return data;
    }

    @Action({ rawError: true })
    public async setIsNextUpdateLading(payload: any) {
        this.SET_NEXT_UPDATE_LADING(payload);
    }
}

export const LadingStore = getModule(LadingModule);
